import axios from "axios";

export const validRequestId = value => /^[0-9a-f]{32}$/.test(String(value ?? ""));
export const canPayRequest = request => request?.can_pay === true
    && !["paid", "pending", "requires_review"].includes(request.payment_status)
    && request.scan_status !== "blocked";

export function createScanWorkflowApi(apiUrl, token) {
    const base = `${apiUrl.replace(/\/+$/, "")}/`;
    const inFlight = new Map();
    const config = signal => {
        if (!token) throw Object.assign(new Error("Authentication required"), { code: "authentication" });
        return { withCredentials: true, signal, timeout: 15_000, headers: { Accept: "application/json", Authorization: `Bearer ${token}` } };
    };
    const requestPath = id => {
        if (!validRequestId(id)) throw new Error("Invalid request ID");
        return `${base}scan/requests/${encodeURIComponent(id)}`;
    };
    const requestData = data => {
        if (!data || !validRequestId(data.request_id)) throw new Error("Invalid request response");
        return data;
    };
    // Only overlapping calls are shared. A subsequent purchase is a NEW POST.
    const once = (key, action) => {
        if (inFlight.has(key)) return inFlight.get(key);
        const operation = Promise.resolve().then(action).finally(() => inFlight.delete(key));
        inFlight.set(key, operation);
        return operation;
    };
    return {
        create(dataScan) {
            return once("envelope", async () => {
                const data_scan = Object.fromEntries(["firstName", "lastName", "email", "company", "url", "ips"].map(key => [key, dataScan[key]]));
                const { data } = await axios.post(`${base}scan/envelopes`, { data_scan }, config());
                return requestData(data);
            });
        },
        async list(signal) {
            const { data } = await axios.get(`${base}scan/requests`, config(signal));
            if (!Array.isArray(data?.requests) || !Number.isInteger(data.page) || !Number.isInteger(data.page_size) || data.page_size <= 0 || !Number.isInteger(data.total)) throw new Error("Invalid history response");
            data.requests.forEach(requestData);
            return data;
        },
        async get(id, signal) {
            const { data } = await axios.get(requestPath(id), config(signal));
            if (String(requestData(data).request_id) !== String(id)) throw new Error("Mismatched request");
            return data;
        },
        async resendSignature(id) {
            const { data } = await axios.post(
                `${requestPath(id)}/resend-signature`,
                undefined,
                config()
            );

            if (
                !data
                || String(data.request_id) !== String(id)
                || typeof data.envelope_id !== "string"
                || data.signature_status !== "sent"
                || data.status !== "signature_resent"
            ) {
                throw new Error("Invalid signature resend response");
            }

            return data;
        },
        async progress(id, signal, progressUrl) {
            const url = progressUrl ? new URL(progressUrl, base) : new URL(`${requestPath(id)}/progress`);
            if (url.origin !== new URL(base).origin || url.username || url.password) throw new Error("Invalid progress URL");
            const { data } = await axios.get(
                url.href,
                config(signal)
            );

            if (!data || String(data.request_id ?? "") !== String(id)) {
                throw new Error("Mismatched scan progress");
            }

            const allowedStatuses = [
                "awaiting_payment",
                "pending_creation",
                "pending",
                "queued",
                "running",
                "completed",
                "failed",
                "launching",
                "launch_unknown",
                "launch_failed",
                "blocked",
                "unknown",
            ];

            if (!allowedStatuses.includes(data.status)) {
                throw new Error("Invalid scan progress status");
            }

            const percentage = data.percentage == null ? null : Number(data.percentage);

            if (
                percentage !== null && (!Number.isFinite(percentage)
                || percentage < 0
                || percentage > 100)
            ) {
                throw new Error("Invalid scan progress percentage");
            }

            return {
                ...data,
                percentage,
            };
        },
        checkout(id) {
            return once(`checkout:${id}`, async () => {
                const { data } = await axios.post(`${requestPath(id)}/checkout`, undefined, config());
                const url = new URL(data?.checkout_url);
                if (url.protocol !== "https:" || url.username || url.password) throw new Error("Invalid Checkout URL");
                return { ...data, checkout_url: url.href };
            });
        },
        async payment(sessionId, signal) {
            if (typeof sessionId !== "string" || !sessionId.trim()) throw new Error("Missing session ID");
            const { data } = await axios.get(`${base}scan/stripe/status`, { ...config(signal), params: { session_id: sessionId } });
            requestData(data);
            if (data.session_id && data.session_id !== sessionId) throw new Error("Mismatched payment session");
            return data;
        },
    };
}
